import React, { useState, useEffect } from 'react';
import LogoutButton from './LogoutButton';
import buildInfo from '../build-info.json';

// Available themes
const AVAILABLE_THEMES = [
  { id: 'cscca-2025', name: 'CSCCA 2025 (Default)' },
];

// Define the settings interface
export interface AppSettings {
  enableTopScoreAnimation: boolean;
  enableShakeAnimation: boolean;
  forceTopScoreAnimation: boolean;
  useAutomaticMetricLabel: boolean;
  customMetricLabel?: string;
  theme: string;
  envOverrides: {
    [key: string]: string;
  };
}

// Props for the Settings component
interface SettingsProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  onSaveSettings: (settings: AppSettings) => void;
}

// Default settings
export const defaultSettings: AppSettings = {
  // Can be overridden with REACT_APP_ENABLE_TOP_SCORE_ANIMATION
  enableTopScoreAnimation: true,
  // Can be overridden with REACT_APP_ENABLE_SHAKE_ANIMATION
  enableShakeAnimation: true,
  // Can be overridden with REACT_APP_FORCE_TOP_SCORE_ANIMATION
  forceTopScoreAnimation: false,
  // Can be overridden with REACT_APP_USE_AUTOMATIC_METRIC_LABEL
  useAutomaticMetricLabel: true,
  customMetricLabel: "RELATIVE PEAK STRIKE FORCE",
  theme: "cscca-2025", // Default theme
  envOverrides: {}
};

// Environment variables that can be overridden
const OVERRIDABLE_ENV_VARS = [
  'REACT_APP_FEMALE_GROUP_ID',
  'REACT_APP_MALE_GROUP_ID',
  'REACT_APP_METRIC_ID',
  'REACT_APP_ORG_ID',
  'REACT_APP_TEAM_ID',
  'REACT_APP_TEST_TYPE',
  'REACT_APP_LEADERBOARD_START',
  'REACT_APP_LEADERBOARD_END',
  'REACT_APP_ENABLE_TOP_SCORE_ANIMATION',
  'REACT_APP_ENABLE_SHAKE_ANIMATION',
  'REACT_APP_FORCE_TOP_SCORE_ANIMATION',
  'REACT_APP_USE_AUTOMATIC_METRIC_LABEL'
];

const Settings: React.FC<SettingsProps> = ({ isOpen, onClose, settings, onSaveSettings }) => {
  const [localSettings, setLocalSettings] = useState<AppSettings>({ ...settings });
  const [activeTab, setActiveTab] = useState<'animations' | 'environment' | 'account'>('animations');
  const [envVarValues, setEnvVarValues] = useState<{[key: string]: string}>({});

  // Initialize settings and env var values
  useEffect(() => {
    setLocalSettings({ ...settings });
    
    // Initialize env var values with current env values or overrides
    const initialEnvValues: {[key: string]: string} = {};
    OVERRIDABLE_ENV_VARS.forEach(envVar => {
      // Use override if it exists, otherwise use the env value
      initialEnvValues[envVar] = settings.envOverrides[envVar] || process.env[envVar] || '';
    });
    setEnvVarValues(initialEnvValues);
  }, [settings, isOpen]);

  const handleToggleChange = (setting: keyof Omit<AppSettings, 'envOverrides'>) => {
    setLocalSettings({
      ...localSettings,
      [setting]: !localSettings[setting]
    });
  };

  const handleEnvVarChange = (key: string, value: string) => {
    // Update the displayed value
    setEnvVarValues({
      ...envVarValues,
      [key]: value
    });
    
    // Determine if this is an override
    const isOverride = value !== (process.env[key] || '');
    
    // If it matches the env value, remove the override
    const newOverrides = { ...localSettings.envOverrides };
    if (isOverride) {
      newOverrides[key] = value;
    } else {
      delete newOverrides[key];
    }
    
    // Update settings with new overrides
    setLocalSettings({
      ...localSettings,
      envOverrides: newOverrides
    });
  };

  const handleSave = () => {
    onSaveSettings(localSettings);
    onClose();
  };

  const handleReset = () => {
    const confirmReset = window.confirm('Are you sure you want to reset all settings to default?');
    if (confirmReset) {
      onSaveSettings(defaultSettings);
      setLocalSettings({ ...defaultSettings });
      
      // Reset env values to actual env values
      const resetEnvValues: {[key: string]: string} = {};
      OVERRIDABLE_ENV_VARS.forEach(envVar => {
        resetEnvValues[envVar] = process.env[envVar] || '';
      });
      setEnvVarValues(resetEnvValues);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Scoreboard Settings</h2>
          <div className="build-number">Build: {buildInfo.buildNumber}</div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="settings-tabs">
          <button 
            className={`tab-button ${activeTab === 'animations' ? 'active' : ''}`} 
            onClick={() => setActiveTab('animations')}
          >
            Appearance
          </button>
          <button 
            className={`tab-button ${activeTab === 'environment' ? 'active' : ''}`} 
            onClick={() => setActiveTab('environment')}
          >
            System
          </button>
          <button 
            className={`tab-button ${activeTab === 'account' ? 'active' : ''}`} 
            onClick={() => setActiveTab('account')}
          >
            User
          </button>
        </div>

        <div className="settings-content">
          {activeTab === 'animations' && (
            <div className="animation-settings">
              <div className="setting-group">
                <h3>Theme</h3>
                <div className="setting-item">
                  <label htmlFor="themeSelector">Select Theme:</label>
                  <select
                    id="themeSelector"
                    value={localSettings.theme}
                    onChange={(e) => {
                      setLocalSettings({
                        ...localSettings,
                        theme: e.target.value
                      });
                    }}
                    className="select-input"
                  >
                    {AVAILABLE_THEMES.map(theme => (
                      <option key={theme.id} value={theme.id}>
                        {theme.name}
                      </option>
                    ))}
                  </select>
                  <p className="setting-description">
                    Changes the visual style of the scoreboard
                  </p>
                </div>
              </div>
              
              <div className="setting-divider"></div>
            
              <div className="setting-item">
                <label>
                  <input 
                    type="checkbox" 
                    checked={localSettings.enableTopScoreAnimation} 
                    onChange={() => handleToggleChange('enableTopScoreAnimation')} 
                  />
                  Enable Top Score Animation
                </label>
              </div>
              <div className="setting-item">
                <label>
                  <input 
                    type="checkbox" 
                    checked={localSettings.enableShakeAnimation} 
                    onChange={() => handleToggleChange('enableShakeAnimation')} 
                  />
                  Enable Shake Animation
                </label>
              </div>
              <div className="setting-item special-setting">
                <label>
                  <input 
                    type="checkbox" 
                    checked={localSettings.forceTopScoreAnimation} 
                    onChange={() => handleToggleChange('forceTopScoreAnimation')} 
                  />
                  Force Top Score Animation (Everyone's a Winner Mode)
                </label>
                <p className="setting-description">
                  When enabled, all new scores will trigger the top score animation
                </p>
              </div>
              
              <div className="setting-divider"></div>
              
              <div className="setting-group">
                <h3>Metric Label</h3>
                <div className="setting-item">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={localSettings.useAutomaticMetricLabel} 
                      onChange={() => handleToggleChange('useAutomaticMetricLabel')} 
                    />
                    Use Automatic Metric Label
                  </label>
                  <p className="setting-description">
                    When enabled, automatically formats the metric label based on the metric ID
                  </p>
                </div>
                
                {!localSettings.useAutomaticMetricLabel && (
                  <div className="setting-item">
                    <label htmlFor="customMetricLabel">Custom Metric Label:</label>
                    <input
                      id="customMetricLabel"
                      type="text"
                      value={localSettings.customMetricLabel || ''}
                      onChange={(e) => {
                        setLocalSettings({
                          ...localSettings,
                          customMetricLabel: e.target.value
                        });
                      }}
                      placeholder="Enter custom metric label"
                      className="text-input"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'environment' && (
            <div className="environment-settings">
              <p className="setting-description">
                Override environment variables. These values will persist in your browser.
              </p>
              
              <div className="quick-actions">
                <h3>Quick Actions</h3>
                <div className="action-buttons">
                  <button 
                    className="action-button start-now-button"
                    onClick={() => {
                      const currentTime = Date.now();
                      // Update the leaderboard start time
                      handleEnvVarChange('REACT_APP_LEADERBOARD_START', currentTime.toString());
                      // Show alert
                      alert(`Leaderboard start time set to now (${new Date(currentTime).toLocaleString()})`);
                    }}
                  >
                    Start Leaderboard Now
                  </button>
                </div>
                <p className="setting-description">
                  This will effectively "reset" the leaderboard, including only tests performed after the current time.
                </p>
              </div>
              
              <div className="setting-divider"></div>
              
              {OVERRIDABLE_ENV_VARS.map((envVar) => (
                <div 
                  className={`env-override-item ${localSettings.envOverrides[envVar] ? 'has-override' : ''}`} 
                  key={envVar}
                >
                  <label htmlFor={envVar}>{envVar}</label>
                  <input
                    id={envVar}
                    type="text"
                    value={envVarValues[envVar] || ''}
                    onChange={(e) => handleEnvVarChange(envVar, e.target.value)}
                    placeholder="Not set"
                  />
                  {localSettings.envOverrides[envVar] && (
                    <span className="override-indicator">Modified</span>
                  )}
                </div>
              ))}
              <div className="env-info">
                <p>Current time: {new Date().toLocaleString()}</p>
                <p>Time in ms: {Date.now()}</p>
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="account-settings">
              <div className="user-info">
                <h3>Sign Out</h3>
              </div>
              <div className="setting-actions">
                <div className="logout-container">
                  <LogoutButton className="settings-logout-button" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="settings-footer">
          <button className="reset-button" onClick={handleReset}>Reset to Default</button>
          <button className="save-button" onClick={handleSave}>Save Settings</button>
        </div>
      </div>
    </div>
  );
};

export default Settings; 